import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])';

// After a submit the form refused, moves focus to the first field in error
// inside `scope`, in DOM order: the first element marked aria-invalid (a Field
// with an error marks its control), or, for a group such as a pill set whose
// role="group" can't take focus, the group's first control. Call the returned
// function in the same event that sets the errors: focus moves after the
// commit that renders them, including when the errors haven't changed.
export function useFocusFirstError(scope: RefObject<HTMLElement | null>): () => void {
  const [refusals, setRefusals] = useState(0);

  useLayoutEffect(() => {
    if (refusals === 0 || scope.current === null) return;
    const invalid = scope.current.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (invalid === null) return;
    const target = invalid.matches(FOCUSABLE) ? invalid : invalid.querySelector<HTMLElement>(FOCUSABLE);
    invalid.scrollIntoView({ block: 'center' });
    target?.focus({ preventScroll: true });
  }, [refusals, scope]);

  return useCallback(() => setRefusals((n) => n + 1), []);
}
